/*
 OK FBI — AI Interrogation Panel  (P7 · Real 3-Column Desktop Layout)

 Layout:
   • xl (≥1280 px): CSS Grid RTL — [SuspectFile 25%] [Dialogue 50%] [Evidence 25%]
   • md (768-1279 px): Dialogue full-width + two side drawers (ملف / أدلة)
   • sm (<768 px): Tabs — ملف / حوار / أدلة

 Preserves 100% of existing logic: TTS, history, confront, broadcast prompts,
 expand-interrogation (OWNER), emotion badge, ducking bridge, testids.

 OWNER-only Portrait Regeneration button calls the existing endpoint
   POST /api/owner/ai/cases/{case_id}/regenerate  body: {part:"suspect_portrait", suspect_id}
*/
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, BASE_URL } from "@/lib/api";
import { assetUrl } from "@/contexts/SessionContext";
import { useAuth } from "@/pages/AuthPage";
import { toast } from "sonner";
import {
  X, Send, Loader2, ShieldAlert, Sparkles, Fingerprint, Play, Pause,
  Volume2, VolumeX, RefreshCw, FileText, Files, MessageSquare,
} from "lucide-react";

// ─── Emotion → visual style ───────────────────────────────────────────
const EMOTION_STYLES = {
  CALM:       { label: "هادئ",   color: "text-emerald-400", bg: "bg-emerald-950/50", border: "border-emerald-800/60" },
  NERVOUS:    { label: "متوتر",  color: "text-amber-400",   bg: "bg-amber-950/50",   border: "border-amber-800/60" },
  SHOCKED:    { label: "مصدوم",  color: "text-cyan-300",    bg: "bg-cyan-950/50",    border: "border-cyan-800/60" },
  DEFENSIVE:  { label: "دفاعيّ", color: "text-red-400",     bg: "bg-red-950/50",     border: "border-red-800/60" },
  ANGRY:      { label: "غاضب",   color: "text-red-500",     bg: "bg-red-950/70",     border: "border-red-700/70" },
  CONFIDENT:  { label: "واثق",   color: "text-gold",        bg: "bg-yellow-950/40",  border: "border-yellow-800/50" },
  EVASIVE:    { label: "مراوغ",  color: "text-fuchsia-400", bg: "bg-fuchsia-950/50", border: "border-fuchsia-800/60" },
  REGRETFUL:  { label: "نادم",   color: "text-indigo-300",  bg: "bg-indigo-950/50",  border: "border-indigo-800/60" },
};

const SUGGESTIONS = [
  "أين كنت ليلة وقوع الجريمة؟",
  "منذ متى تعرف الضحية؟",
  "من كان معك في ذلك الوقت؟",
  "ما علاقتك بمكان الحدث؟",
  "هل كنت على خلاف مع الضحية؟",
];

function buildBroadcastPrompts(suspect, evidenceList) {
  const name = suspect?.name || "المشتبه به";
  const arr = [
    `من يشك أن ${name} يخفي شيئاً؟`,
    `هل روايته مقنعة برأيكم؟`,
    `أي دليل تريدون مواجهته به؟`,
    `ما التناقض الذي لاحظتموه؟`,
    `هل ${name} هو الجاني؟ صوّتوا!`,
    `من تريدون استجوابه بعده؟`,
  ];
  const first = (evidenceList || [])[0];
  if (first?.name) arr.push(`ماذا لو واجهناه بالدليل: ${first.name}؟`);
  return arr;
}

// ─────────────────────────────────────────────────────────────────────
// Media-query hook so only ONE responsive layout is mounted at a time.
// Avoids duplicate refs (inputRef / scroller) across desktop/tablet/mobile.
function useBreakpoint() {
  const get = () => {
    if (typeof window === "undefined") return "desktop";
    const w = window.innerWidth;
    if (w >= 1280) return "desktop";
    if (w >= 768)  return "tablet";
    return "mobile";
  };
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const onResize = () => setBp(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return bp;
}

export function AIInterrogationPanel({ open, onClose, caseId, suspect }) {
  const [turns, setTurns] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState("");
  const [availableEvidence, setAvailableEvidence] = useState([]);
  const [showConfront, setShowConfront] = useState(false);
  const [ttsReady, setTtsReady] = useState(false);
  const [muted, setMuted] = useState(false);
  const [audioStatus, setAudioStatus] = useState("idle"); // idle | loading | playing | paused | error
  const [audioError, setAudioError] = useState("");
  const [audioVolume, setAudioVolume] = useState(0.9);

  // Responsive helpers (md drawers + mobile tabs)
  const [drawer, setDrawer] = useState(null);         // "file" | "evidence" | null
  const [mobileTab, setMobileTab] = useState("chat"); // "file" | "chat" | "evidence"

  const audioRef = useRef(null);
  const scroller = useRef(null);
  const inputRef = useRef(null);
  const bp = useBreakpoint();

  const currentEmotion = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "assistant" && turns[i].emotion) return turns[i].emotion;
    }
    return "CALM";
  }, [turns]);
  const emoStyle = EMOTION_STYLES[currentEmotion] || EMOTION_STYLES.CALM;

  // ── Load prior session on open ──────────────────────────────────
  useEffect(() => {
    if (!open || !caseId || !suspect?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingHistory(true); setError("");
      try {
        const [hist, evs, tts] = await Promise.all([
          api.get(`/interrogate/history`, { params: { case_id: caseId, suspect_id: suspect.id } }).catch(() => ({ data: {} })),
          api.get(`/interrogate/available-evidence`, { params: { case_id: caseId } }).catch(() => ({ data: [] })),
          api.get(`/tts/status`).catch(() => ({ data: { ready: false } })),
        ]);
        if (cancelled) return;
        setSessionId(hist.data.session_id || null);
        setTurns(Array.isArray(hist.data.turns) ? hist.data.turns : []);
        setAvailableEvidence(Array.isArray(evs.data) ? evs.data : []);
        setTtsReady(!!tts.data?.ready);
      } catch (e) {
        if (!cancelled) setError("تعذّر تحميل السجل السابق");
      } finally {
        if (!cancelled) setLoadingHistory(false);
        setTimeout(() => inputRef.current?.focus(), 120);
      }
    })();
    return () => { cancelled = true; };
  }, [open, caseId, suspect?.id]);

  // ── Audio ducking bridge ────────────────────────────────────────
  const duckStart = () => { try { window.dispatchEvent(new CustomEvent("okfbi:duck:start")); } catch {} };
  const duckEnd   = () => { try { window.dispatchEvent(new CustomEvent("okfbi:duck:end"));   } catch {} };

  const speak = useCallback(async (text, emotion) => {
    if (!ttsReady || muted || !text || !caseId || !suspect?.id) return null;
    setAudioStatus("loading");
    setAudioError("");
    let a = audioRef.current;
    if (!a || !a.tagName) {
      a = document.getElementById("okfbi-interrogation-audio");
      if (!a) {
        a = document.createElement("audio");
        a.id = "okfbi-interrogation-audio";
        a.setAttribute("preload", "auto");
        a.style.display = "none";
        document.body.appendChild(a);
      }
      audioRef.current = a;
    }
    try {
      const res = await fetch(`${BASE_URL}/api/tts/speak`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          case_id: caseId, character_id: suspect.id, text,
          emotion: emotion || "CALM", gender_hint: suspect.gender || "",
        }),
      });
      if (!res.ok) {
        setAudioStatus("error");
        setAudioError(res.status === 503 ? "خدمة الصوت غير متاحة حالياً — النص ما زال متاحاً" : "تعذّر توليد الصوت — يمكنك متابعة النص");
        return null;
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        setAudioStatus("error");
        setAudioError("لم يصل ملف صوت صالح — يمكنك متابعة النص");
        return null;
      }
      const url = URL.createObjectURL(blob);
      try { a.pause(); a.currentTime = 0; } catch {}
      a.src = url;
      a.volume = muted ? 0 : audioVolume;
      a.onplay  = () => { duckStart(); setAudioStatus("playing"); };
      a.onpause = () => { duckEnd(); setAudioStatus("paused"); };
      a.onended = () => {
        duckEnd();
        try { URL.revokeObjectURL(url); } catch {}
        setAudioStatus("idle");
      };
      a.onerror = () => {
        duckEnd();
        setAudioStatus("error");
        setAudioError("تعذّر تشغيل الصوت — يمكنك متابعة النص");
      };
      try {
        await a.play();
      } catch {
        setAudioStatus("error");
        setAudioError("اضغط تشغيل للسماح بالصوت");
      }
      return url;
    } catch {
      setAudioStatus("error");
      setAudioError("تعذّر الاتصال بخدمة الصوت — يمكنك متابعة النص");
      return null;
    }
  }, [ttsReady, muted, audioVolume, caseId, suspect?.id, suspect?.gender]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio || audioStatus === "loading") return;
    if (audioStatus === "playing") audio.pause();
    else if (audioStatus === "paused") audio.play().catch(() => {
      setAudioStatus("error");
      setAudioError("اضغط تشغيل للسماح بالصوت");
    });
  };

  const changeAudioVolume = (value) => {
    const volume = Math.max(0, Math.min(1, Number(value)));
    setAudioVolume(volume);
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
  };

  const toggleAudioMute = () => {
    setMuted((previous) => {
      const next = !previous;
      if (audioRef.current) audioRef.current.volume = next ? 0 : audioVolume;
      return next;
    });
  };

  useEffect(() => {
    if (!open && audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      duckEnd();
      setAudioStatus("idle");
    }
  }, [open]);

  useEffect(() => {
    if (!scroller.current) return;
    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [turns, busy]);

  // ── Send / confront ────────────────────────────────────────────
  const send = useCallback(async () => {
    const msg = input.trim();
    if (!msg || busy || !caseId || !suspect?.id) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text: msg, at: new Date().toISOString() }]);
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/interrogate`, {
        case_id: caseId, suspect_id: suspect.id, message: msg,
        session_id: sessionId || undefined,
      });
      setSessionId(data.session_id);
      setTurns((t) => [...t, {
        role: "assistant", text: data.reply, emotion: data.emotion,
        at: new Date().toISOString(),
      }]);
      speak(data.reply, data.emotion);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "تعذّر توليد الرد الآن");
      setTurns((t) => t.slice(0, -1));
      setInput(msg);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [input, busy, caseId, suspect?.id, sessionId, speak]);

  const confront = useCallback(async (evidence, customMessage) => {
    if (!evidence || busy || !caseId || !suspect?.id) return;
    setShowConfront(false);
    const msg = (customMessage || `أواجهك بهذا الدليل: ${evidence.name}`).trim();
    setTurns((t) => [...t, { role: "user", text: msg,
      evidence_id: evidence.id, evidence_name: evidence.name,
      at: new Date().toISOString() }]);
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/interrogate/confront`, {
        case_id: caseId, suspect_id: suspect.id, evidence_id: evidence.id,
        message: msg, session_id: sessionId || undefined,
      });
      setSessionId(data.session_id);
      setTurns((t) => [...t, {
        role: "assistant", text: data.reply, emotion: data.emotion,
        at: new Date().toISOString(),
      }]);
      speak(data.reply, data.emotion);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "تعذّر توليد الرد الآن");
      setTurns((t) => t.slice(0, -1));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [busy, caseId, suspect?.id, sessionId, speak]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!open || !suspect) return null;

  // ── Panels (reused across breakpoints) ─────────────────────────
  const suspectAside = (
    <SuspectFileAside suspect={suspect} caseId={caseId}
                      emoStyle={emoStyle} emotionLabel={emoStyle.label} />
  );
  const evidenceAside = (
    <EvidenceRailAside evidence={availableEvidence}
                      onConfrontClick={() => setShowConfront(true)}
                      suspect={suspect} />
  );
  const dialogueCol = (
    <DialogueColumn
      suspect={suspect}
      turns={turns}
      loadingHistory={loadingHistory}
      busy={busy}
      error={error}
      currentEmotion={currentEmotion}
      emoStyle={emoStyle}
      ttsReady={ttsReady}
      muted={muted}
      setMuted={setMuted}
      audioStatus={audioStatus}
      audioVolume={audioVolume}
      audioError={audioError}
      togglePlayback={togglePlayback}
      changeAudioVolume={changeAudioVolume}
      toggleAudioMute={toggleAudioMute}
      onClose={onClose}
      caseId={caseId}
      availableEvidence={availableEvidence}
      onConfrontClick={() => setShowConfront(true)}
      onSuggestion={(q) => setInput(q)}
      speak={speak}
      input={input}
      setInput={setInput}
      onKeyDown={onKeyDown}
      send={send}
      inputRef={inputRef}
      scroller={scroller}
      onOpenFileDrawer={bp === "tablet" ? () => setDrawer("file") : null}
      onOpenEvidenceDrawer={bp === "tablet" ? () => setDrawer("evidence") : null}
      mobileTab={mobileTab}
      setMobileTab={setMobileTab}
    />
  );

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md overflow-hidden flex flex-col"
        data-testid="ai-interrogation-panel"
      >
        {/* Cinematic backdrop */}
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ background: "radial-gradient(ellipse at top, rgba(217,166,80,0.15), transparent 60%)" }} />

        <div className="relative h-full w-full max-w-[1600px] mx-auto min-h-0 overflow-hidden flex flex-col flex-1" dir="rtl">
          {/* ══════════════════ Desktop (xl ≥1280): 3-col Grid ══════════════════ */}
          {bp === "desktop" && (
          <div
            className="grid h-full min-h-0 overflow-hidden flex-1"
            style={{ gridTemplateColumns: "25% 50% 25%", gridTemplateRows: "100%" }}
            data-testid="interrogation-desktop-grid"
          >
            <aside className="border-l border-metal bg-black/40 overflow-y-auto h-full min-h-0"
                   data-testid="suspect-file-aside">
              {suspectAside}
            </aside>
            <section className="flex flex-col min-w-0 h-full min-h-0 overflow-hidden"
                     data-testid="dialogue-center-col">
              {dialogueCol}
            </section>
            <aside className="border-r border-metal bg-black/40 overflow-y-auto h-full min-h-0"
                   data-testid="evidence-rail-aside">
              {evidenceAside}
            </aside>
          </div>
          )}

          {/* ══════════════════ Tablet/Laptop (md-lg): dialogue + drawers ══════ */}
          {bp === "tablet" && (
          <div className="flex flex-col h-full min-h-0 overflow-hidden flex-1"
               data-testid="interrogation-tablet-layout">
            {dialogueCol}
          </div>
          )}

          {/* ══════════════════ Mobile (<md): Tabs ══════════════════════════════ */}
          {bp === "mobile" && (
          <div className="flex flex-col h-full min-h-0 overflow-hidden flex-1"
               data-testid="interrogation-mobile-layout">
            <div className="flex border-b border-metal bg-black/60 shrink-0"
                 data-testid="interrogation-mobile-tabs">
              <TabBtn active={mobileTab === "file"}    onClick={() => setMobileTab("file")}
                      testid="tab-file"     icon={<FileText size={14}/>} label="الملف" />
              <TabBtn active={mobileTab === "chat"}    onClick={() => setMobileTab("chat")}
                      testid="tab-chat"     icon={<MessageSquare size={14}/>} label="الحوار" />
              <TabBtn active={mobileTab === "evidence"} onClick={() => setMobileTab("evidence")}
                      testid="tab-evidence" icon={<Files size={14}/>} label="الأدلة" />
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {mobileTab === "file"     && <div className="flex-1 overflow-y-auto min-h-0">{suspectAside}</div>}
              {mobileTab === "chat"     && dialogueCol}
              {mobileTab === "evidence" && <div className="flex-1 overflow-y-auto min-h-0">{evidenceAside}</div>}
            </div>
          </div>
          )}

          {/* ── Tablet drawers (slide-in from sides) ──────────────────── */}
          <AnimatePresence>
            {drawer === "file" && (
              <SideDrawer side="right" onClose={() => setDrawer(null)} testid="drawer-file">
                {suspectAside}
              </SideDrawer>
            )}
            {drawer === "evidence" && (
              <SideDrawer side="left" onClose={() => setDrawer(null)} testid="drawer-evidence">
                {evidenceAside}
              </SideDrawer>
            )}
          </AnimatePresence>

          {/* Evidence confront modal */}
          {showConfront && (
            <EvidencePickerModal
              evidence={availableEvidence}
              onClose={() => setShowConfront(false)}
              onConfront={confront}
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

// ═════════════════════════════════════════════════════════════════════
// Middle column — header, transcript, input
// ═════════════════════════════════════════════════════════════════════
function DialogueColumn({
  suspect, turns, loadingHistory, busy, error,
  currentEmotion, emoStyle, ttsReady, muted, setMuted,
  audioStatus, audioVolume, audioError, togglePlayback, changeAudioVolume, toggleAudioMute,
  onClose, caseId, availableEvidence, onConfrontClick, onSuggestion, speak,
  input, setInput, onKeyDown, send, inputRef, scroller,
  onOpenFileDrawer, onOpenEvidenceDrawer, mobileTab, setMobileTab,
}) {
  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-black/30">
      {/* Header */}
      <div className="flex items-center gap-3 p-3 md:p-4 border-b border-metal shrink-0 bg-black/50">
        {/* Mobile/Tablet drawer toggles */}
        {onOpenFileDrawer && (
        <button
          onClick={onOpenFileDrawer}
          className="p-2 border border-metal text-gold hover:bg-gold/10"
          data-testid="open-file-drawer-btn"
          title="ملف المشتبه"
        >
          <FileText size={16}/>
        </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">الاستجواب الذكي</div>
          <div className="font-cairo text-white text-lg md:text-xl truncate" data-testid="ai-suspect-name">
            {suspect.name}
          </div>
        </div>

        <div className={`px-3 py-1.5 border ${emoStyle.border} ${emoStyle.bg} font-cairo text-xs md:text-sm ${emoStyle.color}`}
             data-testid="ai-emotion-badge" title={currentEmotion}>
          <span className="inline-block w-2 h-2 rounded-full ml-2 align-middle" style={{ background: "currentColor" }} />
          {emoStyle.label}
        </div>

        {ttsReady && (
          <div className="flex items-center gap-1" data-testid="ai-audio-controls">
            {audioStatus === "loading" ? (
              <Loader2 size={16} className="animate-spin text-gold" title="جاري تجهيز الصوت" />
            ) : audioStatus === "playing" ? (
              <button onClick={togglePlayback} data-testid="ai-audio-pause" title="إيقاف مؤقت للصوت" className="p-2 text-gold hover:text-white"><Pause size={16} /></button>
            ) : audioStatus === "paused" ? (
              <button onClick={togglePlayback} data-testid="ai-audio-play" title="تشغيل الصوت" className="p-2 text-gold hover:text-white"><Play size={16} /></button>
            ) : null}
            <button onClick={toggleAudioMute}
              data-testid="ai-mute-btn"
              title={muted ? "تشغيل الصوت" : "كتم الصوت"}
              className="p-2 text-zinc-400 hover:text-gold">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : audioVolume}
              onChange={(event) => changeAudioVolume(event.target.value)}
              data-testid="ai-audio-volume" title="مستوى الصوت"
              className="w-16 accent-yellow-600" />
            {audioError && <span data-testid="ai-audio-error" className="max-w-40 text-[9px] text-amber-300 font-cairo" title={audioError}>الصوت غير متوفر</span>}
          </div>
        )}

        <BroadcastAndOwnerControls caseId={caseId} suspect={suspect}
          evidenceList={availableEvidence} onExpandDone={() => window.location.reload()}/>

        {onOpenEvidenceDrawer && (
        <button
          onClick={onOpenEvidenceDrawer}
          className="p-2 border border-metal text-gold hover:bg-gold/10"
          data-testid="open-evidence-drawer-btn"
          title="الأدلة"
        >
          <Files size={16}/>
        </button>
        )}

        <button onClick={onClose} data-testid="ai-close-btn"
                className="p-2 text-zinc-400 hover:text-white transition-colors">
          <X size={20} />
        </button>
      </div>

      {/* Transcript */}
      <div
        ref={scroller}
        className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 space-y-4 min-h-0 overscroll-contain"
        dir="rtl"
        style={{ scrollBehavior: "smooth" }}
      >
        {loadingHistory && (
          <div className="text-center text-zinc-300 text-sm font-cairo py-6">
            <Loader2 className="inline animate-spin ml-2" size={14} /> جاري تحميل الجلسة السابقة…
          </div>
        )}
        {!loadingHistory && turns.length === 0 && (
          <div className="text-center text-zinc-400 font-cairo max-w-xl mx-auto py-10">
            <div className="text-gold text-[11px] tracking-[0.4em] mb-3">START INTERROGATION</div>
            <div className="text-lg mb-2">اطرح سؤالك الأول</div>
            <div className="text-xs text-zinc-300">
              المشتبه به سيجيبك بشخصيّته. لن يعترف مباشرة، ولن يخرج عن دوره. راقب حالته الشعورية.
            </div>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((q) => (
                <button key={q} onClick={() => onSuggestion(q)}
                        className="text-[11px] font-cairo px-3 py-1.5 border border-metal text-zinc-300 hover:border-gold hover:text-gold transition-colors"
                        data-testid="ai-suggestion-btn">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t, i) => (
          <Bubble key={i} turn={t} suspectName={suspect.name}
            ttsReady={ttsReady} onReplay={() => speak(t.text, t.emotion)} />
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="max-w-[75%] px-4 py-3 bg-zinc-900/70 border border-metal font-cairo text-zinc-400 text-sm inline-flex items-center gap-2">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
              <span className="text-xs">يفكّر…</span>
            </div>
          </div>
        )}
        {error && (
          <div className="max-w-lg mx-auto px-4 py-3 border border-red-900/70 bg-red-950/40 text-red-300 font-cairo text-sm flex items-center gap-2"
               data-testid="ai-error">
            <ShieldAlert size={14} /> {error}
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="border-t border-metal p-3 md:p-4 shrink-0 bg-black/60">
        <div className="flex items-end gap-2" dir="rtl">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 2000))}
            onKeyDown={onKeyDown}
            rows={2}
            disabled={busy}
            placeholder="اكتب سؤالك للمشتبه به… (Enter للإرسال)"
            data-testid="ai-input"
            className="flex-1 bg-zinc-950 border border-metal focus:border-gold text-white font-cairo text-sm px-3 py-2 outline-none resize-none placeholder-zinc-600"
            style={{ direction: "rtl" }}
          />
          <button
            onClick={onConfrontClick}
            disabled={busy || availableEvidence.length === 0}
            data-testid="ai-confront-btn"
            title={availableEvidence.length === 0 ? "اكشف أدلة أولاً" : "واجه بدليل موثّق"}
            className="px-3 py-2 text-sm font-cairo border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
          >
            <Fingerprint size={14} />
            <span className="hidden sm:inline">واجه بدليل</span>
          </button>
          <button
            onClick={send}
            disabled={busy || !input.trim()}
            data-testid="ai-send-btn"
            className="btn-primary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            <span className="hidden sm:inline">إرسال</span>
          </button>
        </div>
        <div className="text-[10px] text-zinc-400 font-cairo mt-2 flex items-center gap-2">
          <Sparkles size={10} className="text-gold/70" />
          مدعوم بالذكاء الاصطناعي (Claude Sonnet 5) — الردود توليدية داخل حدود الشخصية فقط، ولا تكشف الحل.
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Right column — suspect file (portrait + biography + owner regen)
// ═════════════════════════════════════════════════════════════════════
function SuspectFileAside({ suspect, caseId, emoStyle, emotionLabel }) {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";

  return (
    <div className="flex flex-col gap-3 p-4" data-testid="suspect-file-content">
      <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">SUSPECT FILE</div>

      <div className="relative w-full aspect-[4/5] border border-gold/40 overflow-hidden bg-black/70 flex items-center justify-center">
        {suspect.portrait_status === "READY" && suspect.portrait ? (
          <img src={assetUrl(suspect.portrait)} alt=""
               className="w-full h-full object-cover"
               data-testid="suspect-portrait-ready"/>
        ) : suspect.portrait ? (
          <img src={assetUrl(suspect.portrait)} alt="" className="w-full h-full object-cover"/>
        ) : (
          <div data-testid="suspect-portrait-pending"
               className="w-full h-full flex flex-col items-center justify-center text-gold/70 font-cairo text-xs text-center px-2 leading-tight bg-gradient-to-b from-black to-neutral-900">
            <div className="text-3xl opacity-40 mb-2">◐</div>
            <div>الصورة قيد التجهيز</div>
          </div>
        )}
        {isOwner && suspect.portrait_status && (
          <PortraitRegenButton caseId={caseId} suspect={suspect}/>
        )}
      </div>

      <div>
        <div className="font-cairo text-white text-lg leading-tight">{suspect.name}</div>
        <div className="text-xs text-zinc-300 font-cairo">
          {suspect.job || ""}
          {suspect.age ? ` · ${suspect.age} سنة` : ""}
        </div>
        {suspect.suspicion_level && (
          <div className="mt-2 inline-block px-2 py-1 text-[10px] border border-metal font-cairo text-zinc-200"
               data-testid="suspicion-level">
            مستوى الشبهة: {({low:"منخفضة",medium:"متوسطة",high:"عالية"})[suspect.suspicion_level] || suspect.suspicion_level}
          </div>
        )}
      </div>

      <div className={`px-3 py-2 border ${emoStyle.border} ${emoStyle.bg} font-cairo text-xs ${emoStyle.color} flex items-center gap-2`}>
        <span className="w-2 h-2 rounded-full" style={{ background: "currentColor" }} />
        <span>الحالة الشعورية الآن: {emotionLabel}</span>
      </div>

      {suspect.relation && (
        <FileField label="العلاقة بالضحية" value={suspect.relation}/>
      )}
      {suspect.motive && (
        <FileField label="الدافع المحتمل" value={suspect.motive}/>
      )}
      {suspect.alibi_claim && (
        <FileField label="الحجّة (Alibi)" value={suspect.alibi_claim}
                   subtitle={suspect.alibi_status ? `الحالة: ${suspect.alibi_status}` : null}/>
      )}
      {Array.isArray(suspect.timeline) && suspect.timeline.length > 0 && (
        <div>
          <div className="text-[10px] tracking-widest text-gold/80 mb-1 font-mono-ar">TIMELINE</div>
          <ul className="text-[11px] text-zinc-200 font-cairo space-y-1 list-disc pr-4">
            {suspect.timeline.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
      )}

      {suspect.voice_profile && suspect.voice_profile.status === "PENDING_TTS" && (
        <div className="text-[10px] text-zinc-400 font-cairo border border-dashed border-metal p-2"
             data-testid="voice-pending">
          الصوت جاهز لتوليد الصوت عند توفر الخدمة
        </div>
      )}
    </div>
  );
}

function FileField({ label, value, subtitle }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest text-gold/80 mb-0.5 font-mono-ar">{label}</div>
      <div className="text-xs text-zinc-100 font-cairo leading-relaxed">{value}</div>
      {subtitle && <div className="text-[10px] text-zinc-400 font-cairo mt-0.5">{subtitle}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Left column — evidence rail
// ═════════════════════════════════════════════════════════════════════
function EvidenceRailAside({ evidence, onConfrontClick, suspect }) {
  const list = Array.isArray(evidence) ? evidence : [];
  return (
    <div className="flex flex-col gap-3 p-4" data-testid="evidence-rail-content">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">EVIDENCE ({list.length})</div>
        <button onClick={onConfrontClick}
                disabled={list.length === 0}
                data-testid="evidence-rail-confront-btn"
                className="text-[11px] font-cairo px-2 py-1 border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1">
          <Fingerprint size={12}/> واجه بدليل
        </button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-8 text-zinc-400 font-cairo text-xs border border-dashed border-metal"
             data-testid="evidence-rail-empty">
          لا توجد أدلة مكشوفة بعد.<br/>اكشف دليلاً من غرفة العمليات أولاً.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((e) => (
            <div key={e.id}
                 data-testid={`evidence-item-${e.id}`}
                 className="p-2.5 border border-metal bg-black/50 hover:border-gold/60 transition-colors">
              <div className="flex items-start gap-2">
                <Fingerprint size={14} className="text-gold mt-0.5 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-cairo text-sm text-white leading-tight">{e.name}</div>
                  <div className="text-[10px] text-zinc-300 font-cairo mt-0.5">
                    {e.type || ""}{e.importance ? ` • ${e.importance}` : ""}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BroadcastPromptsInline suspect={suspect} evidenceList={list}/>
    </div>
  );
}

function BroadcastPromptsInline({ suspect, evidenceList }) {
  const [expanded, setExpanded] = useState(false);
  const prompts = buildBroadcastPrompts(suspect, evidenceList);
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("تم النسخ"); } catch {}
  };
  return (
    <div className="mt-2 border-t border-metal pt-3">
      <button onClick={() => setExpanded((v) => !v)}
              className="w-full text-right text-[11px] tracking-widest text-gold font-mono-ar mb-2 flex items-center justify-between"
              data-testid="broadcast-inline-toggle">
        <span>BROADCAST PROMPTS</span>
        <span className="text-[9px] opacity-70">{expanded ? "إخفاء" : "عرض"}</span>
      </button>
      {expanded && (
        <div className="space-y-1.5" data-testid="broadcast-inline-list">
          {prompts.map((p, i) => (
            <button key={i} onClick={() => copy(p)}
                    className="w-full text-right text-[11px] font-cairo p-2 border border-metal bg-black/40 text-zinc-200 hover:border-gold/60 hover:text-white transition-colors"
                    data-testid={`broadcast-inline-${i}`}
                    title="اضغط للنسخ">
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// OWNER-only Portrait Regeneration button
// Uses existing endpoint: POST /api/owner/ai/cases/{case_id}/regenerate
// body: { part: "suspect_portrait", suspect_id }
// ═════════════════════════════════════════════════════════════════════
function PortraitRegenButton({ caseId, suspect }) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.stopPropagation();
    if (!caseId || !suspect?.id) return;
    if (!window.confirm(`سيتم إعادة توليد صورة المشتبه "${suspect.name}" بواسطة الذكاء الاصطناعي. متابعة؟`)) return;
    setBusy(true);
    try {
      await api.post(`/owner/ai/cases/${caseId}/regenerate`, {
        part: "suspect_portrait", suspect_id: suspect.id,
      });
      toast.success("تم إعادة توليد الصورة — أعد فتح اللوحة للتحديث");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 || status === 423) {
        toast.error("مركز المالك مقفل — افتح مركز المالك أولاً لفك القفل الإداري");
      } else if (status === 404) {
        toast.error("هذه القضية مبنية مسبقاً ولا يمكن إعادة توليد صورها.");
      } else {
        toast.error(typeof detail === "string" ? detail : "فشل إعادة التوليد");
      }
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="owner-portrait-regen-btn"
      title="إعادة توليد الصورة (OWNER)"
      className="absolute bottom-2 left-2 p-2 rounded-full bg-black/70 border border-gold/60 text-gold hover:bg-gold/20 backdrop-blur-sm disabled:opacity-50"
    >
      {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Chat bubble
// ═════════════════════════════════════════════════════════════════════
function Bubble({ turn, suspectName, ttsReady, onReplay }) {
  const isUser = turn.role === "user";
  const emo = turn.emotion && EMOTION_STYLES[turn.emotion];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div className={`max-w-[80%] px-4 py-3 font-cairo text-sm leading-relaxed border ${
        isUser
          ? "bg-gold/10 border-gold/40 text-white"
          : "bg-zinc-900/70 border-metal text-zinc-100"
      }`}>
        <div className={`text-[10px] tracking-widest mb-1 flex items-center gap-2 ${isUser ? "text-gold/80" : "text-zinc-300"}`}>
          <span>{isUser ? "المحقّق" : suspectName}</span>
          {emo && (
            <span className={`inline-block px-1.5 py-0.5 ${emo.bg} ${emo.color} ${emo.border} border text-[9px]`}>
              {emo.label}
            </span>
          )}
          {!isUser && ttsReady && onReplay && (
            <button onClick={onReplay} title="إعادة تشغيل الصوت"
              data-testid="ai-replay-btn"
              className="ml-auto p-0.5 text-zinc-300 hover:text-gold">
              <Volume2 size={12} />
            </button>
          )}
        </div>
        {turn.evidence_name && (
          <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-1 border border-gold/40 bg-yellow-950/30 text-gold text-[10px] font-cairo">
            <Fingerprint size={11} /> مواجهة بدليل: {turn.evidence_name}
          </div>
        )}
        <div style={{ whiteSpace: "pre-wrap" }}>{turn.text}</div>
      </div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Evidence confrontation modal
// ═════════════════════════════════════════════════════════════════════
function EvidencePickerModal({ evidence, onClose, onConfront }) {
  const [selected, setSelected] = useState(null);
  const [customMsg, setCustomMsg] = useState("");
  return (
    <div className="absolute inset-0 z-10 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} dir="rtl" data-testid="evidence-picker">
      <div className="bg-[#0b0b0e] border border-gold/40 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">EVIDENCE CONFRONTATION</div>
            <div className="font-cairo text-white text-lg">اختر دليلاً موثّقاً للمواجهة</div>
            <div className="text-xs text-zinc-300 font-cairo">لا يمكنك المواجهة إلا بأدلة كُشفت رسمياً في هذه القضية.</div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        {evidence.length === 0 ? (
          <div className="text-center py-8 text-zinc-300 font-cairo text-sm border border-dashed border-metal">
            لا توجد أدلة مكشوفة بعد. اكشف دليلاً من غرفة العمليات أولاً.
          </div>
        ) : (
          <div className="grid gap-2 mb-3">
            {evidence.map((e) => (
              <button key={e.id} onClick={() => setSelected(e.id)}
                data-testid={`evidence-option-${e.id}`}
                className={`flex items-center gap-3 p-3 border text-right transition-colors ${
                  selected === e.id
                    ? "border-gold bg-yellow-950/30"
                    : "border-metal hover:border-gold/60"
                }`}>
                <Fingerprint size={16} className={selected === e.id ? "text-gold" : "text-zinc-300"} />
                <div className="flex-1">
                  <div className="font-cairo text-sm text-white">{e.name}</div>
                  <div className="text-[11px] text-zinc-300 font-cairo">{e.type || ""} {e.importance ? `• ${e.importance}` : ""}</div>
                </div>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <>
            <textarea value={customMsg} onChange={(e) => setCustomMsg(e.target.value.slice(0, 500))}
              placeholder="سؤال أو جملة اختيارية قبل تقديم الدليل… (اتركه فارغاً للتقديم المباشر)"
              className="w-full bg-black/40 border border-metal focus:border-gold p-2 text-white text-sm font-cairo outline-none resize-none min-h-16"
              rows={2} style={{ direction: "rtl" }} />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={onClose} className="text-xs font-cairo px-3 py-2 border border-metal text-zinc-300 hover:text-white">إلغاء</button>
              <button
                onClick={() => onConfront(evidence.find(x => x.id === selected), customMsg)}
                data-testid="confirm-confront-btn"
                className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5">
                <Fingerprint size={12} /> واجه الآن
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Tablet slide-in drawer
// ═════════════════════════════════════════════════════════════════════
function SideDrawer({ side = "right", onClose, children, testid }) {
  const from = side === "right" ? { x: "100%" } : { x: "-100%" };
  return (
    <motion.div
      className="absolute inset-0 z-20 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid={testid}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={from} animate={{ x: 0 }} exit={from}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        className={`absolute top-0 bottom-0 w-[85%] max-w-md bg-neutral-950 border-metal overflow-y-auto ${
          side === "right" ? "right-0 border-l" : "left-0 border-r"
        }`}
      >
        <div className="flex justify-end p-2 border-b border-metal">
          <button onClick={onClose} className="p-2 text-zinc-300 hover:text-white"><X size={16}/></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

function TabBtn({ active, onClick, testid, icon, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 py-3 text-xs font-cairo flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
        active
          ? "border-gold text-gold bg-gold/5"
          : "border-transparent text-zinc-400 hover:text-white"
      }`}
    >
      {icon} {label}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Broadcast prompts modal + OWNER expand-interrogation button
// ═════════════════════════════════════════════════════════════════════
function BroadcastAndOwnerControls({ caseId, suspect, evidenceList, onExpandDone }) {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showExpand, setShowExpand] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [rounds, setRounds] = useState(5);
  const prompts = buildBroadcastPrompts(suspect, evidenceList);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("تم النسخ"); } catch {}
  };

  const doExpand = async () => {
    if (!window.confirm(`سيتم إضافة ${rounds} جولات جديدة لهذا المشتبه فقط باستخدام نفس سياق القضية. متابعة؟`)) return;
    setExpanding(true);
    try {
      const { data } = await api.post(`/owner/ai/cases/${caseId}/regenerate`,
        { part: "expand_interrogation", suspect_id: suspect.id });
      toast.success(`تمت إضافة ${data.added || rounds} جولات`);
      setShowExpand(false);
      onExpandDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التوسيع");
    } finally { setExpanding(false); }
  };

  return (
    <>
      <button onClick={() => setShowBroadcast(true)} data-testid="broadcast-btn"
        title="أسئلة البث"
        className="px-3 py-1.5 text-[11px] font-cairo border border-gold/40 text-gold hover:bg-gold/10 rounded">
        أسئلة البث
      </button>
      {isOwner && (
        <button onClick={() => setShowExpand(true)} data-testid="owner-expand-btn"
          title="توسيع الاستجواب (OWNER)"
          className="px-3 py-1.5 text-[11px] font-cairo border border-purple-500 text-purple-300 hover:bg-purple-950/40 rounded">
          توسيع الاستجواب
        </button>
      )}
      {showBroadcast && (
        <div className="fixed inset-0 z-[1000] bg-black/80 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-neutral-950 border border-gold/40 rounded max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" data-testid="broadcast-modal">
            <div className="p-4 border-b border-metal flex justify-between items-center">
              <h3 className="font-cairo font-black text-gold">أسئلة البث</h3>
              <button onClick={() => setShowBroadcast(false)} className="text-zinc-300 hover:text-white"><X size={16}/></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2 font-cairo">
              {prompts.map((p, i) => (
                <div key={i} className="flex items-center gap-2 p-3 border border-metal bg-black/40 text-white text-sm">
                  <span className="flex-1">{p}</span>
                  <button onClick={() => copy(p)} data-testid={`broadcast-copy-${i}`}
                    className="text-[10px] px-2 py-1 border border-gold/40 text-gold hover:bg-gold/10">
                    نسخ
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showExpand && (
        <div className="fixed inset-0 z-[1000] bg-black/80 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-neutral-950 border border-purple-500/50 rounded max-w-sm w-full p-5" data-testid="expand-modal">
            <h3 className="font-cairo font-black text-purple-300 mb-3">توسيع الاستجواب</h3>
            <p className="text-xs text-zinc-200 font-cairo mb-4">
              يستخدم AI نفس سياق القضية لإنشاء جولات جديدة لهذا المشتبه به فقط.
              الحل النهائي والأدلة الحقيقية لن تتغير.
            </p>
            <label className="text-xs font-cairo text-zinc-300">عدد الجولات الجديدة</label>
            <div className="flex gap-2 mt-2 mb-4">
              {[3,5,10].map(n => (
                <button key={n} onClick={() => setRounds(n)}
                  data-testid={`expand-rounds-${n}`}
                  className={`flex-1 py-2 text-sm font-cairo border ${rounds===n ? "border-purple-400 text-purple-300 bg-purple-950/30" : "border-metal text-zinc-300"}`}>
                  {n}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowExpand(false)} disabled={expanding}
                className="flex-1 py-2 border border-metal text-zinc-200 text-xs font-cairo">إلغاء</button>
              <button onClick={doExpand} disabled={expanding} data-testid="expand-confirm"
                className="flex-1 py-2 bg-purple-800 text-white text-xs font-cairo hover:bg-purple-700 disabled:opacity-50">
                {expanding ? "جاري…" : "تنفيذ"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AIInterrogationPanel;
